import {
  faArrowRight,
  faCalendarDays,
  faCloud,
  faStars,
} from "@awesome.me/kit-794acd4adf/icons/classic/solid";
import {
  faCheckDouble,
  faCommentsQuestionCheck,
  faLandmarkMagnifyingGlass,
  faMessageBot,
  faSparkles,
  faStarship,
  faStarshipFreighter,
  faUsersGear,
} from "@awesome.me/kit-794acd4adf/icons/duotone/solid";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { PopupModal } from "react-calendly";
import {
  alpha,
  AppBar,
  Button,
  Fade,
  Grid2 as Grid,
  Icon,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Switch,
  TextField,
  Toolbar,
  Typography,
  useScrollTrigger,
} from "@mui/material";
import Box from "@mui/material/Box";
import GreyLogo from "./assets/logos/logo-gray.svg";
import BannerBg1 from "./assets/landing/bannerbg1";
import BannerBg2 from "./assets/landing/bannerbg2";
import BannerBg3 from "./assets/landing/bannerbg3";
import { CSSProperties, useEffect, useRef, useState } from "react";
import { Utm } from "react-calendly/typings/calendly";
import { green, pink } from "@mui/material/colors";
import env from "./config/env";

const bannerImages = [BannerBg1, BannerBg2, BannerBg3];

function useInterval(callback: () => void, delay: number) {
  const savedCallback = useRef<typeof callback>(null);

  // Remember the latest callback.
  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  // Set up the interval.
  useEffect(() => {
    function tick() {
      savedCallback?.current?.();
    }
    if (delay !== null) {
      let id = setInterval(tick, delay);
      return () => clearInterval(id);
    }
  }, [delay]);
}

const LandingPage = () => {
  const trigger = useScrollTrigger({
    disableHysteresis: true,
    threshold: 0,
    target: window ? window : undefined,
  });
  const [currentImage, setCurrentImage] = useState(0);
  const [email, setEmail] = useState("");
  const [utm, setUtm] = useState<Utm>({});
  const [billingPeriod, setBillingPeriod] = useState<"mo" | "yr">("mo");
  const [isCalendlyOpen, setIsCalendlyOpen] = useState(false);

  const switchImage = () => {
    setCurrentImage((previousImage) =>
      previousImage < bannerImages.length - 1 ? previousImage + 1 : 0
    );
  };

  useInterval(switchImage, 6000);

  function openCalendly(utmToSend: Utm) {
    setUtm({ ...utmToSend, utmSource: "Landing Page" });
    setIsCalendlyOpen(true);
  }

  return (
    <>
      <Box
        sx={{
          "& *": {
            fontFamily: "Nunito Sans !important",
          },
        }}
      >
        <AppBar
          sx={{
            color: trigger ? "black" : "white",
            backgroundColor: trigger ? "white" : "transparent",
            boxShadow: trigger
              ? "0px 2px 4px -1px rgba(0, 0, 0, 0.2), 0px 4px 5px 0px rgba(0, 0, 0, 0.14), 0px 1px 10px 0px rgba(0, 0, 0, 0.12)"
              : "none",
            display: "flex",
            minHeight: "70px",
          }}
          position="fixed"
        >
          <Toolbar
            sx={{
              margin: "auto",
              width: "100%",
              maxWidth: { xs: "100%", sm: "1196px" },
            }}
          >
            <Box display={"flex"} maxHeight={{ xs: "", sm: "38px" }}>
              <Box
                width={"38px"}
                sx={{ backgroundColor: "#FFF", borderRadius: "50%" }}
              >
                <img alt="stratahub-logo" height={"100%"} src={GreyLogo} />
              </Box>
              <Box marginLeft={"10px"} flexGrow={1} alignContent={"center"}>
                StrataHub . <FontAwesomeIcon icon={faCloud} />
              </Box>
            </Box>
            <Box flexGrow={1} />
            <Box
              marginRight={"50px"}
              sx={{
                "& button": {
                  color: trigger ? undefined : "white !important",
                  fontWeight: 600,
                },
              }}
              display={{ xs: "none", md: "flex" }}
              gap={"20px"}
            >
              <Button
                onClick={() => {
                  document
                    .getElementById("features")
                    ?.scrollIntoView({ behavior: "smooth" });
                }}
              >
                Features
              </Button>
              <Button
                onClick={() => {
                  document
                    .getElementById("pricing")
                    ?.scrollIntoView({ behavior: "smooth" });
                }}
              >
                Pricing
              </Button>
            </Box>
            <Box textAlign={"end"}>
              <Button
                sx={{ backgroundColor: "#5795c6", padding: "10px 24px" }}
                variant="contained"
                onClick={() =>
                  (window.location.href = `https://app.${
                    env.APP_ENV === "dev" ? "dev." : ""
                  }stratahub.cloud`)
                }
              >
                Login
              </Button>
            </Box>
          </Toolbar>
        </AppBar>
        <Box
          height={"750px"}
          position={"absolute"}
          top={0}
          zIndex={50}
          width={"100%"}
          sx={{
            "::before": {
              content: '""',
              zIndex: "10",
              width: "100%",
              height: "100%",
              display: { xs: "none", sm: "block" },
              position: "absolute",
              background:
                "linear-gradient(90deg, rgba(33,36,41,255) 0%, rgba(33,36,41,255) 30%, rgba(33,36,41,0) 87%, rgba(33,36,41,0) 100%)",
            },
            "& img": {
              objectFit: "cover",
            },
          }}
        >
          {bannerImages.map((src, idx) => (
            <Fade
              key={`bannerimg-${idx}`}
              in={idx === currentImage}
              timeout={2000}
            >
              <img
                fetchPriority={idx === bannerImages.length - 1 ? "high" : "low"}
                loading={idx === bannerImages.length - 1 ? "eager" : "lazy"}
                alt={`Banner image ${idx + 1}`}
                style={{ position: "absolute", top: 0 }}
                width={"100%"}
                height={"750px"}
                sizes="(max-width: 500px) 527px, 100vw"
                srcSet={Object.keys(src)
                  .map(
                    (sizeKey) =>
                      `${src[sizeKey as keyof typeof src]} ${sizeKey}`
                  )
                  .join(", ")}
              />
            </Fade>
          ))}
        </Box>
        <Box
          height={"750px"}
          marginX={"auto"}
          width={"100%"}
          maxWidth={{ xs: "100%", sm: "1196px" }}
          paddingX={{ xs: "28px", sm: "0px" }}
          paddingTop={"350px"}
          position={"relative"}
          zIndex={75}
        >
          <Box>
            <Typography
              color="white"
              align="center"
              fontSize={"30px"}
              fontWeight={700}
              lineHeight={"120%"}
              letterSpacing={"-0.05em"}
              display={{ xs: "inherit", sm: "none" }}
              sx={{ textShadow: "2px 2px 20px black, -2px -2px 20px black" }}
            >
              Software that keeps coucil organized while council keeps owners
              content
            </Typography>
            <Typography
              color="white"
              fontSize={"44px"}
              lineHeight={"120%"}
              letterSpacing={"-0.05em"}
              display={{ xs: "none", sm: "inherit" }}
            >
              Software that keeps coucil organized
            </Typography>
            <Typography
              color="white"
              fontSize={"44px"}
              lineHeight={"120%"}
              letterSpacing={"-0.05em"}
              display={{ xs: "none", sm: "inherit" }}
            >
              while you keep your owners content
            </Typography>
            <Grid container marginTop={"40px"} gap={"10px"}>
              <Grid size={{ xs: 12, sm: 5, md: 5, lg: 4 }}>
                <TextField
                  slotProps={{
                    input: {
                      sx: { backgroundColor: "#fff" },
                    },
                  }}
                  variant="outlined"
                  placeholder="Email"
                  fullWidth
                  value={email}
                  onKeyDown={({ code }) => {
                    if (
                      code === "Enter" &&
                      /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
                    ) {
                      openCalendly({ utmContent: "landing-banner" });
                    }
                  }}
                  onChange={({ target: { value: newEmail } }) =>
                    setEmail(newEmail)
                  }
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 4, md: 3, lg: 2 }}>
                <Button
                  sx={{
                    padding: "10px 24px",
                    fontSize: { xs: "16px", sm: "20px" },
                    textTransform: "none",
                    backgroundColor: "#e24e4f",
                  }}
                  variant="contained"
                  fullWidth
                  onClick={() => {
                    if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
                      openCalendly({ utmContent: "landing-banner" });
                    }
                  }}
                >
                  Get Started
                </Button>
              </Grid>
            </Grid>
          </Box>
        </Box>

        {/* 
      
              Below Banner Content 
      
      */}
        <Box flexDirection={"column"} display={"flex"}>
          <Box
            maxWidth={{ xs: "100%", sm: "1196px" }}
            paddingX={{ xs: "28px", sm: "0px" }}
            marginX={"auto"}
            width={"100%"}
          >
            {/* Feature Cards */}
            <Box id={"features"} display={"flex"} marginY={"50px"}>
              <Typography
                marginX={"auto"}
                fontSize={"32px"}
                fontWeight={700}
                variant="h2"
              >
                Invest in your success
              </Typography>
            </Box>
            <Grid
              container
              spacing={"20px"}
              width={"100%"}
              height={{ xs: undefined, md: "480px" }}
            >
              {[
                {
                  title: "Preserve Continuity",
                  icon: faLandmarkMagnifyingGlass,
                  body: `Ensure smooth transitions between council members 
                without missing a beat. Securely store historical 
                records, conversations, and contacts all in one place, 
                so new members can seamlessly pick up where others left 
                off without disruptions.`,
                },
                {
                  title: "Simplify Communication",
                  icon: faMessageBot,
                  body: `Cut down on email and text by centralizing everything 
                in one platform. Owners can easily see updates and make requests, 
                ensuring clear, streamlined collaboration.`,
                },
                {
                  title: "Gain Efficiency",
                  icon: faUsersGear,
                  body: `Most council members volunteer their time, making it a valuable and 
                limited resource. Our app helps you streamline tasks, reduce inefficiencies, 
                and focus on maximizing impact and achieving results with 
                every minute spent.`,
                },
                {
                  title: "Ongoing Discussions",
                  icon: faCommentsQuestionCheck,
                  body: `Give owners a forum to raise and discuss concerns anytime—no 
                need to wait for yearly AGMs. Keep conversations active and 
                address issues as they arise.`,
                },
              ].map(({ icon, title, body }) => (
                <Grid
                  key={title.replace(/\s/g, "_")}
                  size={{ xs: 12, md: 3 }}
                  height={"100%"}
                >
                  <Box
                    width={"100%"}
                    height={"100%"}
                    padding={"10px"}
                    border={"1px solid rgba(0, 0, 0, 0.05)"}
                    display={"flex"}
                    flexDirection={"column"}
                  >
                    <Box
                      bgcolor={"rgb(33,36,41)"}
                      padding={"8px"}
                      color={"#fff"}
                      fontSize={"45px"}
                      width={"65px"}
                      height={"65px"}
                    >
                      <FontAwesomeIcon
                        width="65px"
                        style={
                          {
                            "--fa-primary-color": "#5795c6",
                            "--fa-secondary-color": "#ffffff",
                            "--fa-secondary-opacity": "1",
                          } as CSSProperties
                        }
                        opacity={1}
                        fixedWidth
                        icon={icon}
                      />
                    </Box>
                    <Box>
                      <Typography
                        fontSize={"22px"}
                        letterSpacing={"-0.03em"}
                        fontWeight={700}
                        variant="h3"
                        marginY={"15px"}
                      >
                        {title}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography
                        fontSize={"20px"}
                        fontWeight={400}
                        marginBottom={"15px"}
                        align="left"
                      >
                        {body}
                      </Typography>
                    </Box>
                    <Box
                      display={"flex"}
                      flexGrow={1}
                      alignItems={"end"}
                      alignSelf={"end"}
                      sx={{ cursor: "pointer" }}
                      onClick={() =>
                        openCalendly({ utmContent: title.replace(/\s/g, "-") })
                      }
                    >
                      Learn More{" "}
                      <FontAwesomeIcon
                        style={{ marginLeft: "5px" }}
                        icon={faArrowRight}
                      />
                    </Box>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>
          <Box
            maxWidth={{ xs: "100%", sm: "1196px" }}
            paddingX={{ xs: "28px", sm: "0px" }}
            marginX={"auto"}
          >
            {/* Pricing Cards */}
            <Box
              id={"pricing"}
              display={"flex"}
              marginTop={"50px"}
              marginBottom={"20px"}
            >
              <Typography
                marginX={"auto"}
                fontSize={"32px"}
                fontWeight={700}
                variant="h2"
              >
                Pricing that won't break the bank
              </Typography>
            </Box>
            <Box display={"flex"} width={"100%"}>
              <Box display={"flex"} marginX={"auto"}>
                <Typography
                  aria-hidden={billingPeriod === "yr"}
                  aria-description={"Monthly pricing selected"}
                  alignContent={"center"}
                >
                  Monthly
                </Typography>
                <Switch
                  checked={billingPeriod === "yr"}
                  onChange={() =>
                    setBillingPeriod((previous) =>
                      previous === "mo" ? "yr" : "mo"
                    )
                  }
                  inputProps={{
                    "aria-label":
                      "Switch from monthly to yearly billing options",
                  }}
                  sx={{
                    "& .MuiSwitch-switchBase": {
                      color: green[600],
                      "&:hover": {
                        backgroundColor: alpha(green[600], 0.1),
                      },
                    },
                    "& .MuiSwitch-switchBase.Mui-checked": {
                      color: pink[600],
                      "&:hover": {
                        backgroundColor: alpha(pink[600], 0.1),
                      },
                    },
                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                      backgroundColor: pink[600],
                    },
                    "& .MuiSwitch-switchBase + .MuiSwitch-track": {
                      backgroundColor: green[600],
                    },
                  }}
                />
                <Typography
                  aria-hidden={billingPeriod === "mo"}
                  aria-description={"Annual pricing selected"}
                  alignContent={"center"}
                >
                  Annual
                </Typography>
              </Box>
            </Box>
            <Grid
              container
              spacing={"20px"}
              width={"100%"}
              height={{ xs: undefined, md: "480px" }}
            >
              {[
                {
                  title: "Testing things out",
                  price: {
                    mo: 60,
                    yr: 650,
                  },
                  cta: "Start Onboarding",
                  ctaIcon: faStars,
                  points: [
                    "Maintain a copy of different documents",
                    "Accept owner requests via secure form",
                    "Maintain a list of owners, tenants, pets, and emergency contacts",
                    "Dedicated forum space for open discussion",
                  ],
                },
                {
                  title: "Fully decked out",
                  price: {
                    mo: 100,
                    yr: 1000,
                  },
                  cta: "Start Onboarding",
                  ctaIcon: faStars,
                  plus: ["Everything in basic"],
                  points: [
                    "Unlimited document version storage",
                    "Unlimited SMS alerts to owners",
                    "Generate agendas for council meetings based on open issues/projects",
                  ],
                },
                {
                  title: "Something custom",
                  cta: "Book a Call",
                  ctaIcon: faCalendarDays,
                  body: (
                    <Box
                      paddingX={"20px"}
                      marginTop={"50px"}
                      display={"flex"}
                      flexDirection={"column"}
                      gap={"20px"}
                    >
                      <Typography>
                        Need something you don’t see here? It’s likely already
                        on our roadmap—or we can add it for you!
                      </Typography>
                      <Typography>
                        We’re dedicated to collaborating with you to ensure our
                        platform meets your needs, providing the tools and
                        features that make the biggest impact for you.
                      </Typography>
                      <Typography>
                        <i>
                          Some examples of features coming soon: infraction
                          tracking, payment acceptance, and accounting
                        </i>
                      </Typography>
                    </Box>
                  ),
                },
              ].map(({ title, body, plus, price, points, cta, ctaIcon }) => (
                <Grid
                  key={title.replace(/\s/g, "_")}
                  size={{ xs: 12, md: 4 }}
                  height={"100%"}
                >
                  <Box
                    width={"100%"}
                    height={"100%"}
                    padding={"15px"}
                    border={"1px solid rgba(0, 0, 0, 0.05)"}
                    borderRadius={"25px"}
                    display={"flex"}
                    flexDirection={"column"}
                  >
                    <Box>
                      <Typography
                        fontSize={"28px"}
                        letterSpacing={"-0.03em"}
                        fontWeight={700}
                        variant="h3"
                        marginY={"15px"}
                        align="center"
                      >
                        {title}
                      </Typography>
                    </Box>
                    {price && (
                      <Box marginX={"auto"}>
                        <Typography fontSize={"24px"}>
                          ${price[billingPeriod]} /{" "}
                          {billingPeriod === "mo" ? "month" : "year"}
                        </Typography>
                      </Box>
                    )}
                    <Box>
                      {points && (
                        <List>
                          {plus && (
                            <ListItem>
                              <ListItemIcon>
                                <Icon>
                                  <FontAwesomeIcon icon={faCheckDouble} />
                                </Icon>
                              </ListItemIcon>
                              <ListItemText>{plus}</ListItemText>
                            </ListItem>
                          )}
                          {points.map((point) => (
                            <ListItem key={point.replace(/\s/g, "_")}>
                              <ListItemIcon>
                                <Icon>
                                  <FontAwesomeIcon
                                    style={
                                      {
                                        "--fa-primary-color": "#dfb715",
                                        "--fa-secondary-color": "#ffcc00",
                                        "--fa-secondary-opacity": "1",
                                      } as CSSProperties
                                    }
                                    opacity={1}
                                    icon={faSparkles}
                                  />
                                </Icon>
                              </ListItemIcon>
                              <ListItemText>{point}</ListItemText>
                            </ListItem>
                          ))}
                        </List>
                      )}
                      {plus && <Typography></Typography>}
                      {body && typeof body !== "string" && body}
                      {body && typeof body === "string" && (
                        <Typography
                          fontSize={"20px"}
                          fontWeight={400}
                          marginBottom={"15px"}
                          align="left"
                        >
                          {body}
                        </Typography>
                      )}
                    </Box>
                    <Box flexGrow={1} />
                    <Button
                      variant="contained"
                      sx={{ textTransform: "none" }}
                      onClick={() =>
                        openCalendly({
                          utmContent: [
                            title.replace(/\s/g, "_"),
                            billingPeriod,
                          ].join("-"),
                        })
                      }
                    >
                      <Typography>
                        {cta}
                        <FontAwesomeIcon
                          style={{ alignSelf: "center", marginLeft: "5px" }}
                          icon={ctaIcon}
                        />
                      </Typography>
                    </Button>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>
        </Box>
      </Box>
      <Box marginTop={"30px"} display={"flex"}>
        <Box
          marginLeft={"auto"}
          marginRight={"40px"}
          display={"flex"}
          fontSize={"10px"}
        >
          <FontAwesomeIcon
            icon={faStarshipFreighter}
            style={{ alignSelf: "center" }}
          />
          <Typography
            marginX={"10px"}
            fontSize={"10px"}
            alignContent={"center"}
          >
            Built by some nerds from beautiful BC
          </Typography>
          <FontAwesomeIcon icon={faStarship} style={{ alignSelf: "center" }} />
        </Box>
      </Box>
      <PopupModal
        url="https://calendly.com/support-oguk/stratahub"
        open={isCalendlyOpen}
        onModalClose={() => {
          setIsCalendlyOpen(false);
          setUtm({});
        }}
        prefill={{
          email: /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) ? email : undefined,
        }}
        utm={utm}
        pageSettings={{}}
        rootElement={
          document.getElementById("root") || document.createElement("div")
        }
      />
    </>
  );
};

export default LandingPage;
